import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/contexts/CartContext';
import { useProducts } from '@/contexts/ProductsContext';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import './cart-v2.css';

// New-design cart drawer (shown when useSiteDesign().isNew), in the draft's
// look: free-delivery progress, items with quantity steppers, the 3-Step Set
// offer when it isn't in the bag yet, and a cash-on-delivery checkout.

const SET_SLUG = '3-step-set';
const FREE_DELIVERY = 3000;
const toNumber = (price: string) => Number(price.replace(/[^0-9.]/g, '')) || 0;
const tk = (n: number) => '৳' + n.toLocaleString('en-US');

function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {d.split('|').map((p) => (
        <path key={p} d={p} />
      ))}
    </svg>
  );
}
const BAG = 'M5 8h14l-1 12H6z|M9 8V6a3 3 0 0 1 6 0v2';

export default function CartDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { cart, isCartOpen, closeCart, removeFromCart, updateQty, addToCart, totalItems, totalPrice } = useCart();
  const { products, routineSet } = useProducts();

  useEffect(() => {
    if (!isCartOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeCart();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isCartOpen, closeCart]);

  if (!isCartOpen) return null;

  const lineOf = (slug: string) => {
    if (slug === SET_SLUG) {
      if (!routineSet) return null;
      return {
        name: routineSet.designV2?.name || routineSet.name,
        sub: t('cartV2.giftBox', 'Gift box · 3 steps'),
        image: routineSet.image,
        price: toNumber(routineSet.price),
        href: prefix + '/3-step-treatment',
      };
    }
    const p = products.find((x) => x.id === slug);
    if (!p) return null;
    return {
      name: p.designV2?.name || p.name,
      sub: [p.stepLabel, p.volume.replace('ml', ' ml')].filter(Boolean).join(' · '),
      image: p.image,
      price: toNumber(p.price),
      href: `${prefix}/products/${p.id}`,
    };
  };
  const left = Math.max(0, FREE_DELIVERY - totalPrice);
  const offerSet = routineSet && !cart.some((i) => i.slug === SET_SLUG) && cart.length > 0;
  const go = (to: string) => {
    closeCart();
    navigate(to);
  };

  return (
    <div className="v2c">
      <div className="cshade" onClick={closeCart} />
      <aside className="cdrawer" role="dialog" aria-modal="true" aria-label={t('cartV2.title', 'Your bag')}>
        <div className="chead">
          <h2>
            {t('cartV2.title', 'Your bag')} <span>({totalItems})</span>
          </h2>
          <button type="button" className="cx" aria-label={t('common.close', 'Close')} onClick={closeCart}>
            <Icon d="M6 6l12 12M18 6L6 18" />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="cempty">
            <span className="cic">
              <Icon d={BAG} />
            </span>
            <h3>{t('cartV2.emptyTitle', 'Your bag is empty')}</h3>
            <p>{t('cartV2.emptyText', 'Start with the 3-step ritual, or pick the step your skin needs most.')}</p>
            <button type="button" className="cbtn wine" onClick={() => go(prefix + '/')}>
              {t('cartV2.shop', 'Shop products')}
            </button>
            {products.length > 0 && (
              <div className="cpop">
                {products.slice(0, 3).map((p) => (
                  <Link key={p.id} to={`${prefix}/products/${p.id}`} onClick={closeCart}>
                    <img src={p.image} alt="" />
                    <span>{p.designV2?.name || p.name}</span>
                    <b>{p.price}</b>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="cfree">
              <p>
                {left > 0 ? (
                  <>
                    {t('cartV2.awayPre', 'You are')} <b>{tk(left)}</b> {t('cartV2.awayPost', 'away from free delivery')}
                  </>
                ) : (
                  <b>{t('cartV2.free', 'Your order ships free')}</b>
                )}
              </p>
              <span className="cbar">
                <i style={{ width: `${Math.min(100, (totalPrice / FREE_DELIVERY) * 100)}%` }} />
              </span>
            </div>

            <div className="clist">
              {cart.map((item) => {
                const l = lineOf(item.slug);
                if (!l) return null;
                return (
                  <article key={item.slug} className="citem">
                    <Link to={l.href} onClick={closeCart} className="cimg">
                      <img src={l.image} alt={l.name} />
                    </Link>
                    <div className="cbody">
                      <div className="ctop">
                        <Link to={l.href} onClick={closeCart}>
                          <b>{l.name}</b>
                          {l.sub && <small>{l.sub}</small>}
                        </Link>
                        <span className="cprice">{tk(l.price * item.qty)}</span>
                      </div>
                      <div className="crow">
                        <div className="cqty">
                          <button type="button" aria-label="Less" onClick={() => updateQty(item.slug, item.qty - 1)}>
                            −
                          </button>
                          <output>{item.qty}</output>
                          <button type="button" aria-label="More" onClick={() => updateQty(item.slug, item.qty + 1)}>
                            +
                          </button>
                        </div>
                        <button type="button" className="crm" onClick={() => removeFromCart(item.slug)}>
                          {t('common.remove', 'Remove')}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}

              {offerSet && routineSet && (
                <div className="coffer">
                  <img src={routineSet.image} alt="" />
                  <div>
                    <small>{t('cartV2.offerKicker', 'Complete the ritual')}</small>
                    <b>{routineSet.designV2?.name || routineSet.name}</b>
                    <span>
                      {routineSet.price}
                      {routineSet.savings ? ` · ${t('cartV2.save', 'Save')} ${routineSet.savings}` : ''}
                    </span>
                  </div>
                  <button type="button" className="cbtn ghost" onClick={() => addToCart(SET_SLUG, 1)}>
                    {t('cartV2.add', 'Add')}
                  </button>
                </div>
              )}
            </div>

            <div className="cfoot">
              <div className="csum">
                <span>{t('cart.subtotal', 'Subtotal')}</span>
                <b>{tk(totalPrice)}</b>
              </div>
              <p className="cnote">{t('cartV2.delivery', 'Delivery ৳70 in Dhaka, ৳120 outside · free over ৳3,000. Calculated at checkout.')}</p>
              <button type="button" className="cbtn wine" onClick={() => go('/checkout')}>
                <span>{t('common.checkout', 'Checkout')}</span>
                <small>{t('cartV2.cod', 'Cash on delivery')}</small>
              </button>
              <button type="button" className="ccont" onClick={closeCart}>
                {t('common.continueShopping', 'Continue shopping')}
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
